import React, { useState, useEffect } from 'react';
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext';
import { 
  Settings as SettingsIcon, Globe, Shield, Users, Save, AlertTriangle, RefreshCw, Coins, UserPlus, Trash2, Clock, MessageCircle, FileText, Printer, Bluetooth, Lock
} from 'lucide-react';

const Settings = () => {
  const { 
    currentUser, resetSystem, 
    funds, updateFunds, 
    staffList, addStaff, removeStaff,
    permissions, updatePermissions,
    globalCurrency, updateCurrency,
    sysConfig, updateSysConfig
  } = useData();

  const { t, lang, setLang } = useLang(); 

  const isMaster = currentUser?.type === 'Master_Admin';

  const [timezone, setTimezone] = useState('Asia/Manila (GMT+8)');
  const [currency, setCurrency] = useState(globalCurrency);

  const [tempFunds, setTempFunds] = useState({ cash: 0, cashChips: 0, rollingChips: 0 });

  // 로컬 설정 상태
  const [localConfig, setLocalConfig] = useState(sysConfig || {});

  useEffect(() => { setTempFunds(funds); }, [funds]);
  useEffect(() => { setCurrency(globalCurrency); }, [globalCurrency]);
  useEffect(() => { if (sysConfig) setLocalConfig(sysConfig); }, [sysConfig]);

  // ★ [수정됨] 마스터 어드민 비밀번호 강제 인증 (a168168!)
  const verifyMasterPassword = (actionName) => {
      const input = prompt(`[Security Check] \nTo proceed with '${actionName}', please enter Master Admin Password:`);
      
      // ★ 요청하신 비밀번호 설정
      const MASTER_PW = "a168168!";

      if (input === MASTER_PW) {
          return true;
      } else {
          alert("❌ Incorrect Password! Action cancelled.");
          return false;
      }
  };

  const handleFundChange = (key, val) => {
      setTempFunds({ ...tempFunds, [key]: Number(val.replace(/[^0-9]/g, '')) });
  };

  const handleConfigChange = (section, key, value) => {
      if (section) {
          setLocalConfig(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
      } else {
          setLocalConfig(prev => ({ ...prev, [key]: value }));
      }
  };

  const handleLogoUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
              setLocalConfig(prev => ({ ...prev, logoData: reader.result }));
          };
          reader.readAsDataURL(file);
      }
  };

  const connectBluetooth = async () => {
      if (!navigator.bluetooth) {
          alert("Bluetooth is not supported in this browser. (Chrome required)");
          return;
      }
      try {
          const device = await navigator.bluetooth.requestDevice({
              acceptAllDevices: true,
              optionalServices: ['battery_service'] 
          });
          alert(`${t('msg_connect_success')} (${device.name})`);
          setLocalConfig(prev => ({ ...prev, bluetoothDevice: { name: device.name, id: device.id } }));
      } catch (error) {
          console.error(error);
          alert(t('msg_connect_fail'));
      }
  };

  const [permState, setPermState] = useState(permissions);
  
  // 역할 목록 (세분화됨)
  const roles = ['Senior Manager', 'Manager', 'Staff', 'Group', 'Agent'];
  
  const features = [
      { key: 'ShowCash', label: 'Dashboard Cash View' },
      { key: 'CreateAgent', label: 'Create Agent' },
      { key: 'CreateGame', label: 'Create Game' },
      { key: 'Settle', label: 'Settlement' },
      { key: 'CreditApprove', label: 'Credit Approval' },
  ];

  const togglePermission = (featureKey, role) => {
      setPermState(prev => ({ ...prev, [featureKey]: { ...prev[featureKey], [role]: !prev[featureKey][role] } }));
  };

  const [newStaff, setNewStaff] = useState({ code: '', name: '', password: '', role: 'Manager' });

  const handleAddStaff = () => {
      if(!newStaff.code || !newStaff.name || !newStaff.password) return alert("Please fill all fields.");
      addStaff(newStaff);
      setNewStaff({ code: '', name: '', password: '', role: 'Manager' });
  };

  const handleSaveAll = () => {
      // Fund 변경 감지 시에만 비밀번호 확인
      const isFundChanged = JSON.stringify(funds) !== JSON.stringify(tempFunds);
      
      if (isFundChanged) {
          if (!verifyMasterPassword("Fund Update")) {
              return; 
          }
      }

      updateFunds(tempFunds);
      updatePermissions(permState);
      updateCurrency(currency); 
      updateSysConfig(localConfig); 
      
      alert(`${t('save')} ${t('active')}!`); 
  };

  const handleResetData = () => {
    if (window.confirm('⚠️ WARNING: SYSTEM RESET?\n\nAll data will be lost permanently.')) {
      // 초기화 실행 시 비밀번호 확인
      if (verifyMasterPassword("Factory Reset")) {
          resetSystem(); 
      }
    }
  };

  const fmt = (n) => n ? Number(n).toLocaleString() : '0';

  return (
    <div style={{ padding: '30px', backgroundColor: '#f8fafc', minHeight: '100vh', color: '#1e293b' }}>
      
      <h2 style={{ display:'flex', alignItems:'center', gap:'10px', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', color: '#0f172a' }}>
        <SettingsIcon size={28} /> {t('setting_title')}
      </h2>

      {/* 1. 기본 환경 설정 */}
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#334155' }}><Globe size={20}/> {t('sec_env')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div>
                <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('lbl_lang')}</label>
                <select value={lang} onChange={e=>setLang(e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1' }}>
                    <option value="KR">한국어 (Korean)</option>
                    <option value="EN">English</option>
                    <option value="CN">中文 (Chinese)</option>
                    <option value="JP">日本語 (Japanese)</option>
                </select>
            </div>
            <div>
                <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('lbl_time')}</label>
                <div style={{ position: 'relative' }}>
                    <Clock size={16} style={{ position:'absolute', top:'12px', left:'10px', color:'#64748b' }}/>
                    <select value={timezone} onChange={e=>setTimezone(e.target.value)} style={{ width:'100%', padding:'10px 10px 10px 35px', borderRadius:'6px', border:'1px solid #cbd5e1' }}>
                        <option>Asia/Manila (GMT+8)</option>
                        <option>Asia/Seoul (GMT+9)</option>
                        <option>Asia/Shanghai (GMT+8)</option>
                        <option>Asia/Tokyo (GMT+9)</option>
                    </select>
                </div>
            </div>
            <div>
                <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('lbl_curr')}</label>
                <select value={currency} onChange={e=>setCurrency(e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1' }}>
                    <option>PHP</option><option>USD</option><option>HKD</option><option>CNY</option><option>KRW</option><option>JPY</option>
                </select>
            </div>
        </div>
        <div style={{ marginTop:'20px' }}>
             <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('lbl_comp_name')}</label>
             <input value={localConfig.companyName} onChange={e => handleConfigChange(null, 'companyName', e.target.value)} placeholder="My Casino Junket" style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1' }} />
        </div>
        <div style={{ marginTop:'20px' }}>
             <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('lbl_comp_logo')}</label>
             <div style={{ display:'flex', gap:'20px', alignItems:'center' }}>
                 {localConfig.logoData && <img src={localConfig.logoData} alt="Logo" style={{ height:'50px', border:'1px solid #eee' }} />}
                 <input type="file" accept="image/*" onChange={handleLogoUpload} />
             </div>
        </div>
      </div>

      {/* 2. 기기 및 출력 설정 */}
      {isMaster && (
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
          <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#0ea5e9' }}><Printer size={20}/> {t('sec_print')}</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'30px', marginBottom:'20px' }}>
              <div style={{ padding:'15px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                  <h4 style={{ margin:'0 0 10px 0', color:'#334155', fontSize:'14px' }}>{t('lbl_sheet')}</h4>
                  <div style={{ display:'flex', gap:'10px' }}>
                      <select value={localConfig.printerSettings?.settlement?.paperSize} onChange={(e) => handleConfigChange('printerSettings', 'settlement', { ...localConfig.printerSettings.settlement, paperSize: e.target.value })} style={{ flex:1, padding:'8px', borderRadius:'4px', border:'1px solid #cbd5e1' }}>
                          <option value="A4">A4 (Standard)</option>
                          <option value="Letter">Letter</option>
                      </select>
                      <select value={localConfig.printerSettings?.settlement?.orientation} onChange={(e) => handleConfigChange('printerSettings', 'settlement', { ...localConfig.printerSettings.settlement, orientation: e.target.value })} style={{ flex:1, padding:'8px', borderRadius:'4px', border:'1px solid #cbd5e1' }}>
                          <option value="portrait">Portrait</option>
                          <option value="landscape">Landscape</option>
                      </select>
                  </div>
              </div>
              <div style={{ padding:'15px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                  <h4 style={{ margin:'0 0 10px 0', color:'#334155', fontSize:'14px' }}>{t('lbl_slip')}</h4>
                  <div style={{ display:'flex', gap:'10px' }}>
                      <select value={localConfig.printerSettings?.slip?.paperSize} onChange={(e) => handleConfigChange('printerSettings', 'slip', { ...localConfig.printerSettings.slip, paperSize: e.target.value })} style={{ flex:1, padding:'8px', borderRadius:'4px', border:'1px solid #cbd5e1' }}>
                          <option value="A5">A5 (Half)</option>
                          <option value="80mm">Thermal 80mm</option>
                          <option value="58mm">Thermal 58mm</option>
                      </select>
                      <select value={localConfig.printerSettings?.slip?.orientation} onChange={(e) => handleConfigChange('printerSettings', 'slip', { ...localConfig.printerSettings.slip, orientation: e.target.value })} style={{ flex:1, padding:'8px', borderRadius:'4px', border:'1px solid #cbd5e1' }}>
                          <option value="portrait">Portrait</option>
                          <option value="landscape">Landscape</option>
                      </select>
                  </div>
              </div>
          </div>
          <div style={{ padding:'15px', background:'#f0f9ff', borderRadius:'8px', border:'1px solid #bae6fd', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <Bluetooth size={24} color="#0284c7" />
                  <div>
                      <div style={{ fontWeight:'bold', color:'#0369a1' }}>{t('lbl_sig')}</div>
                      <div style={{ fontSize:'12px', color:'#0284c7' }}>
                          {localConfig.bluetoothDevice?.name ? `Connected: ${localConfig.bluetoothDevice.name}` : 'No Device'}
                      </div>
                  </div>
              </div>
              <button onClick={connectBluetooth} style={{ padding:'8px 15px', background:'#0284c7', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('btn_connect')}</button>
          </div>
      </div>
      )}

      {/* 3. 텔레그램 설정 */}
      {isMaster && (
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
          <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#0ea5e9' }}><MessageCircle size={20}/> Telegram Bot Settings</h3>
          <div style={{ marginBottom: '10px' }}>
              <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Bot Token</label>
              <input value={localConfig.telegramBotToken} onChange={e => handleConfigChange(null, 'telegramBotToken', e.target.value)} placeholder="123456789:ABCDefGHiJklmNoPQrStUvWxYz" style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontFamily:'monospace' }} />
          </div>
      </div>
      )}

      {/* 4. 문서 양식 설정 */}
      {isMaster && (
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
          <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#8b5cf6' }}><FileText size={20}/> Document Templates</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'30px' }}>
              <div style={{ padding:'20px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop:0, color:'#334155' }}>Settlement Sheet</h4>
                  <div style={{ marginBottom:'10px' }}><label style={{ fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px' }}>Header Color</label><input type="color" value={localConfig.settlementTemplate?.headerColor || '#000000'} onChange={e => handleConfigChange('settlementTemplate', 'headerColor', e.target.value)} style={{ width:'100%', height:'40px', cursor:'pointer' }} /></div>
                  <div style={{ marginBottom:'10px' }}><label style={{ fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px' }}>Footer Text</label><input value={localConfig.settlementTemplate?.footerText || ''} onChange={e => handleConfigChange('settlementTemplate', 'footerText', e.target.value)} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px' }} /></div>
                  <div style={{ marginBottom:'10px' }}><label style={{ fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px' }}>Terms & Conditions</label><textarea value={localConfig.settlementTemplate?.terms || ''} onChange={e => handleConfigChange('settlementTemplate', 'terms', e.target.value)} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', resize:'none' }} rows="3" /></div>
                  <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'13px', cursor:'pointer' }}><input type="checkbox" checked={localConfig.settlementTemplate?.showLogo || false} onChange={e => handleConfigChange('settlementTemplate', 'showLogo', e.target.checked)} /> Show Logo</label>
              </div>
              <div style={{ padding:'20px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop:0, color:'#334155' }}>Slip (Receipt)</h4>
                  <div style={{ marginBottom:'10px' }}><label style={{ fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px' }}>Header Color</label><input type="color" value={localConfig.slipTemplate?.headerColor || '#000000'} onChange={e => handleConfigChange('slipTemplate', 'headerColor', e.target.value)} style={{ width:'100%', height:'40px', cursor:'pointer' }} /></div>
                  <div style={{ marginBottom:'10px' }}><label style={{ fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px' }}>Footer Text</label><input value={localConfig.slipTemplate?.footerText || ''} onChange={e => handleConfigChange('slipTemplate', 'footerText', e.target.value)} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px' }} /></div>
                  <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'13px', cursor:'pointer' }}><input type="checkbox" checked={localConfig.slipTemplate?.showLogo || false} onChange={e => handleConfigChange('slipTemplate', 'showLogo', e.target.checked)} /> Show Logo</label>
              </div>
          </div>
      </div>
      )}

      {/* 5. Fund Set-up (보안 적용) */}
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#334155' }}>
            <Coins size={20}/> Fund Set-up (Initial Balance) <Lock size={16} color="#ef4444"/>
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Cash</label><input value={fmt(tempFunds.cash)} onChange={e=>handleFundChange('cash', e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontWeight:'bold', color:'#1e293b' }} /></div>
            <div><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Cash Chips</label><input value={fmt(tempFunds.cashChips)} onChange={e=>handleFundChange('cashChips', e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontWeight:'bold', color:'#0284c7' }} /></div>
            <div><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Rolling Chips</label><input value={fmt(tempFunds.rollingChips)} onChange={e=>handleFundChange('rollingChips', e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontWeight:'bold', color:'#d97706' }} /></div>
        </div>
      </div>

      {/* 6. Permissions (역할 세분화 적용) */}
      {isMaster && (
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#334155' }}><Shield size={20}/> Permissions</h3>
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead><tr style={{ background: '#f1f5f9', color: '#475569' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Features</th>
                    {roles.map(role => (<th key={role} style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>{role}</th>))}
                </tr></thead>
                <tbody>{features.map(feat => (
                    <tr key={feat.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#334155' }}>{feat.label}</td>
                        {roles.map(role => (<td key={role} style={{ padding: '12px', textAlign: 'center' }}><input type="checkbox" checked={permState[feat.key]?.[role] || false} onChange={() => togglePermission(feat.key, role)} style={{ width:'16px', height:'16px', cursor:'pointer' }} /></td>))}
                    </tr>
                ))}</tbody>
            </table>
        </div>
      </div>
      )}

      {/* 7. Staff Management (역할 세분화 적용) */}
      {isMaster && (
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '40px' }}>
        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:0, color: '#334155' }}><Users size={20}/> Staff Management</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems:'flex-end' }}>
            <div style={{ flex:1 }}><label style={{ display:'block', fontSize:'12px', color:'#64748b' }}>ID (Code)</label><input value={newStaff.code} onChange={e=>setNewStaff({...newStaff, code:e.target.value})} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'6px' }} /></div>
            <div style={{ flex:1 }}><label style={{ display:'block', fontSize:'12px', color:'#64748b' }}>Name</label><input value={newStaff.name} onChange={e=>setNewStaff({...newStaff, name:e.target.value})} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'6px' }} /></div>
            <div style={{ flex:1 }}><label style={{ display:'block', fontSize:'12px', color:'#64748b' }}>Password</label><input type="password" value={newStaff.password} onChange={e=>setNewStaff({...newStaff, password:e.target.value})} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'6px' }} /></div>
            <div style={{ flex:1 }}><label style={{ display:'block', fontSize:'12px', color:'#64748b' }}>Role</label>
                <select value={newStaff.role} onChange={e=>setNewStaff({...newStaff, role:e.target.value})} style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'6px' }}>
                    <option>Senior Manager</option>
                    <option>Manager</option>
                    <option>Staff</option>
                </select>
            </div>
            <button onClick={handleAddStaff} style={{ padding:'9px 20px', background:'#3b82f6', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}><UserPlus size={18}/> Add</button>
        </div>
        <div style={{ borderTop:'1px solid #e2e8f0', paddingTop:'15px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 50px', fontWeight:'bold', color:'#475569', marginBottom:'10px', fontSize:'13px' }}><div>ID</div><div>Name</div><div>Role</div><div>Action</div></div>
            {staffList.map(s => (<div key={s.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 50px', padding:'10px 0', borderBottom:'1px solid #f1f5f9', fontSize:'13px', alignItems:'center' }}><div style={{ fontWeight:'bold', color:'#0f172a' }}>{s.code}</div><div>{s.name}</div><div><span style={{ padding:'3px 8px', borderRadius:'12px', background: s.role.includes('Manager')?'#dbeafe':'#f1f5f9', color: s.role.includes('Manager')?'#1e40af':'#475569', fontSize:'11px', fontWeight:'bold' }}>{s.role}</span></div><button onClick={()=>removeStaff(s.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444' }}><Trash2 size={16}/></button></div>))}
        </div>
      </div>
      )}

      {/* 하단 저장 버튼 */}
      <div style={{ position:'sticky', bottom:'20px', display:'flex', justifyContent:'flex-end' }}>
         <button onClick={handleSaveAll} style={{ padding: '15px 40px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '30px', fontWeight: 'bold', fontSize:'16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow:'0 10px 25px -5px rgba(37, 99, 235, 0.4)' }}><Save size={20} /> {t('save')}</button>
      </div>

      {/* 시스템 초기화 (보안 적용) */}
      {isMaster && (
        <section style={{ backgroundColor: '#fee2e2', padding: '30px', borderRadius: '12px', border: '1px solid #fca5a5', marginTop:'50px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px', color: '#991b1b' }}><AlertTriangle size={28} /><h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>System Reset <Lock size={20}/></h3></div>
          <p style={{ color: '#b91c1c', marginBottom: '25px', fontSize: '14px' }}>⚠️ WARNING: All data (Agents, Games, Logs) will be permanently deleted.</p>
          <button onClick={handleResetData} style={{ padding: '12px 25px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}><RefreshCw size={18} /> Factory Reset</button>
        </section>
      )}
    </div>
  );
};

export default Settings;